import {
  canEditBoard,
  CARD_COLOUR_HEX,
  CARD_COLOUR_LABELS,
  CARD_COLOURS,
  ICON_LABELS,
  LIMITS,
  mergeLabels,
  newId,
  newWorkflowNode,
  nowIso,
  RELATIONSHIP_LABELS,
  RELATIONSHIP_TYPES,
  WORKFLOW_LIMITS,
  WORKFLOW_NODE_SHAPE_LABELS,
  WORKFLOW_SWIMLANE_TITLE,
  nodeLabels,
  normalize,
  planWorkflowUpdate,
  UNPLACED_COLUMN_TITLE,
  workflowToSnapshot,
  type CardColour,
  type NodeCore,
  type ProjectLabels,
  type RelationshipType,
  type UpdateSummary,
  type WorkflowEdge,
  type WorkflowGraph,
  type WorkflowLinks,
  type WorkflowNode,
  type WorkflowNodeShape,
} from '@blueprint/domain'
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { AppVersion } from '../../components/AppVersion.js'
import { FeedbackTab } from '../feedback/FeedbackTab.js'
import { BrandLockup } from '../../components/BrandLockup.js'
import { Button, ColourSwatches, Field, InlineTitle, Select, TextArea, TextInput } from '../../components/controls.js'
import { Dialog } from '../../components/Dialog.js'
import { IconSvg, NodeCoreFields, useProjectLabels } from '../../components/ItemFields.js'
import { UserMenu } from '../../app/UserMenu.js'
import { getRepository, useBoardStore } from '../../state/board-store.js'
import { useThemeStore } from '../../state/theme-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { useWorkflowStore } from '../../state/workflow-store.js'
import { MenuItem } from '../mind-map/MindmapView.js'
// The page chrome (top bar, footer, states) is the mindmap's, so the two editors match.
import chrome from '../mind-map/MindmapView.module.css'
import styles from './WorkflowView.module.css'

/** Everything a node stores but its id, shape and position, which the canvas keeps itself. */
type NodeData = Omit<WorkflowNode, 'id' | 'shape' | 'x' | 'y'>
type FlowNode = Node<NodeData, WorkflowNodeShape>
/** The connection's own label and meaning; the canvas label shows one or the other. */
type EdgeData = { label: string; kind: RelationshipType | null }
type FlowEdge = Edge<EdgeData>

const EDGE_DEFAULTS = { type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } } as const

/** A typed connection is dashed, and shows its type when it has no label of its own. */
function toFlowEdge(edge: Pick<WorkflowEdge, 'id' | 'source' | 'target' | 'label' | 'type'>, selected = false): FlowEdge {
  return {
    ...EDGE_DEFAULTS,
    id: edge.id,
    source: edge.source,
    target: edge.target,
    selected,
    label: edge.label || (edge.type ? RELATIONSHIP_LABELS[edge.type] : ''),
    style: edge.type ? { strokeDasharray: '6 4' } : undefined,
    data: { label: edge.label, kind: edge.type },
  }
}

function toFlow(graph: WorkflowGraph): { nodes: FlowNode[]; edges: FlowEdge[] } {
  return {
    nodes: graph.nodes.map(({ id, shape, x, y, ...data }) => ({ id, type: shape, position: { x, y }, data })),
    edges: graph.edges.map((edge) => toFlowEdge(edge)),
  }
}

function toGraph(nodes: FlowNode[], edges: FlowEdge[]): WorkflowGraph {
  // A relationship to a node that has been deleted goes with it.
  const ids = new Set(nodes.map((node) => node.id))
  return {
    nodes: nodes.map((node) => ({
      ...node.data,
      relationships: node.data.relationships.filter((relationship) => ids.has(relationship.targetId)),
      id: node.id,
      shape: node.type ?? 'step',
      x: node.position.x,
      y: node.position.y,
    })),
    edges: edges.map((edge) => ({ id: edge.id, source: edge.source, target: edge.target, label: edge.data?.label ?? '', type: edge.data?.kind ?? null })),
  }
}

/** A node's own colour, from the card palette; Default keeps its shape's colours. */
function colourStyle(colour: CardColour): CSSProperties | undefined {
  return colour === 'default' ? undefined : { background: `var(--card-${colour})`, borderColor: `var(--card-${colour}-edge)` }
}

/** The node's icon, then its title. */
function NodeLabel({ data }: { data: NodeData }) {
  return (
    <span className={styles.label}>
      {data.icon ? (
        <span className={styles.icon} title={ICON_LABELS[data.icon]}>
          <IconSvg icon={data.icon} size={14} />
        </span>
      ) : null}
      {data.title}
    </span>
  )
}

/** Left to right, like a story map's backbone. Loaded on first use: it is large. */
async function tidyPositions(nodes: FlowNode[], edges: FlowEdge[]): Promise<Map<string, { x: number; y: number }>> {
  const { default: ELK } = await import('elkjs/lib/elk.bundled.js')
  const layout = await new ELK().layout({
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '40',
      'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    children: nodes.map((node) => ({ id: node.id, width: node.measured?.width ?? 160, height: node.measured?.height ?? 60 })),
    edges: edges.map((edge) => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
  })
  return new Map((layout.children ?? []).map((child) => [child.id, { x: child.x ?? 0, y: child.y ?? 0 }]))
}

function StartNode({ data, selected }: NodeProps<FlowNode>) {
  return (
    <div className={`${styles.node} ${styles.start} ${selected ? styles.selected : ''}`} style={colourStyle(data.colour)}>
      <NodeLabel data={data} />
      <Handle type="source" position={Position.Right} />
    </div>
  )
}

function EndNode({ data, selected }: NodeProps<FlowNode>) {
  return (
    <div className={`${styles.node} ${styles.end} ${selected ? styles.selected : ''}`} style={colourStyle(data.colour)}>
      <Handle type="target" position={Position.Left} />
      <NodeLabel data={data} />
    </div>
  )
}

function StepNode({ data, selected }: NodeProps<FlowNode>) {
  return (
    <div className={`${styles.node} ${styles.step} ${selected ? styles.selected : ''}`} style={colourStyle(data.colour)} title={data.description || undefined}>
      <Handle type="target" position={Position.Left} />
      <NodeLabel data={data} />
      <Handle type="source" position={Position.Right} />
    </div>
  )
}

function DecisionNode({ data, selected }: NodeProps<FlowNode>) {
  const colour = data.colour === 'default' ? undefined : { fill: `var(--card-${data.colour})`, stroke: `var(--card-${data.colour}-edge)` }
  return (
    <div className={`${styles.decision} ${selected ? styles.selected : ''}`} title={data.description || undefined}>
      <Handle type="target" position={Position.Left} />
      <svg className={styles.diamond} style={colour} viewBox="0 0 140 90" preserveAspectRatio="none" aria-hidden="true">
        <polygon points="70,1 139,45 70,89 1,45" />
      </svg>
      <span className={styles.decisionText}>
        <NodeLabel data={data} />
      </span>
      <Handle type="source" position={Position.Right} />
    </div>
  )
}

const NODE_TYPES = { start: StartNode, end: EndNode, step: StepNode, decision: DecisionNode }

/** The workflow page: top bar and toolbar, the canvas with a details panel, and the footer with the save state. */
export function WorkflowView() {
  return (
    <ReactFlowProvider>
      <WorkflowEditor />
    </ReactFlowProvider>
  )
}

function WorkflowEditor() {
  const phase = useWorkflowStore((state) => state.phase)
  const name = useWorkflowStore((state) => state.name)
  const graph = useWorkflowStore((state) => state.graph)
  const access = useWorkflowStore((state) => state.access)
  const loads = useWorkflowStore((state) => state.loads)
  const workflowId = useWorkflowStore((state) => state.id)
  const canUndo = useWorkflowStore((state) => state.canUndo)
  const canRedo = useWorkflowStore((state) => state.canRedo)
  const focusNodeId = useWorkflowStore((state) => state.focusNodeId)
  const projectLabels = useProjectLabels(workflowId)
  /** The graph as it was when "Create storymap" was opened; null while closed. */
  const [generating, setGenerating] = useState<WorkflowGraph | null>(null)
  /** The story maps generated from this workflow, for "Open storymap". */
  const [links, setLinks] = useState<WorkflowLinks>({ boards: [] })
  const [storymapMenu, setStorymapMenu] = useState(false)
  const setView = useUiStore((state) => state.setView)
  const mode = useThemeStore((state) => state.mode)
  const editable = canEditBoard(access)
  const flow = useReactFlow<FlowNode, FlowEdge>()

  const [nodes, setNodes] = useState<FlowNode[]>([])
  const [edges, setEdges] = useState<FlowEdge[]>([])
  const loadedFor = useRef<number | null>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  /** Set by an edit worth saving; the effect below sends the state it produced. */
  const pending = useRef(false)
  /** The undo step the pending edit belongs to (see `change`); none makes it a step of its own. */
  const pendingGroup = useRef<string | undefined>(undefined)
  /** Bumped by every focus in the details panel, so typing into one field until it loses focus is one undo step. */
  const typing = useRef(0)
  /** One Delete reports the removal of the node and of its connections separately: one group for both. */
  const removal = useRef<string | null>(null)

  // Take the graph afresh on every load from the server, never on our own saves.
  useEffect(() => {
    if (!graph || loadedFor.current === loads) return
    loadedFor.current = loads
    const next = toFlow(graph)
    setNodes(next.nodes)
    setEdges(next.edges)
    requestAnimationFrame(() => void flow.fitView({ padding: 0.2 }))
  }, [graph, loads, flow])

  // Arriving from a storymap card: select its node and bring it to the middle at 100%, as the mindmap does.
  useEffect(() => {
    if (!focusNodeId || phase !== 'ready' || loadedFor.current !== loads) return
    setNodes((current) => current.map((node) => ({ ...node, selected: node.id === focusNodeId })))
    setEdges((current) => current.map((edge) => ({ ...edge, selected: false })))
    // After the load's own fit, which is queued the same way.
    requestAnimationFrame(() => void flow.fitView({ nodes: [{ id: focusNodeId }], minZoom: 1, maxZoom: 1 }))
    useWorkflowStore.getState().clearFocus()
  }, [focusNodeId, phase, loads, flow])

  useEffect(() => {
    if (!pending.current) return
    pending.current = false
    const group = pendingGroup.current
    pendingGroup.current = undefined
    useWorkflowStore.getState().change(toGraph(nodes, edges), undefined, group)
  }, [nodes, edges])

  const commit = (group?: string) => {
    pending.current = true
    pendingGroup.current = group
  }

  /** While a text field in the details panel has focus, its edits are one undo step. */
  const textGroup = () => {
    const active = document.activeElement
    const typed = active instanceof HTMLTextAreaElement || (active instanceof HTMLInputElement && active.type !== 'checkbox' && active.type !== 'radio')
    return typed ? `text-${typing.current}` : undefined
  }

  /** The same group for every removal reported in this turn of the event loop. */
  const removalGroup = () => {
    if (!removal.current) {
      removal.current = `remove-${newId()}`
      queueMicrotask(() => {
        removal.current = null
      })
    }
    return removal.current
  }

  /** Shows a graph from the undo history; the store has already taken it and saves it. */
  const restore = useCallback((restored: WorkflowGraph | null) => {
    if (!restored) return
    const next = toFlow(restored)
    setNodes(next.nodes)
    setEdges(next.edges)
  }, [])

  // The mindmap's undo shortcuts, while no text field or dialog has the keys.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && (target.closest('input, textarea, select, [contenteditable="true"]') || target.closest('dialog'))) return
      if (document.querySelector('dialog[open]') || useUiStore.getState().confirm) return
      if (event.key === 'Escape') {
        setStorymapMenu(false)
        return
      }
      if (!editable || !(event.metaKey || event.ctrlKey)) return
      if (event.key === 'z' || event.key === 'Z') {
        event.preventDefault()
        restore(event.shiftKey ? useWorkflowStore.getState().redo() : useWorkflowStore.getState().undo())
      } else if (event.key === 'y') {
        event.preventDefault()
        restore(useWorkflowStore.getState().redo())
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [editable, restore])

  useEffect(() => {
    if (phase !== 'ready' || !workflowId) return
    let cancelled = false
    getRepository()
      .listWorkflowLinks(workflowId)
      .then((next) => !cancelled && setLinks(next))
      // The menu is a convenience; the workflow works without it.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [phase, workflowId])

  // Any click outside the menu closes it.
  useEffect(() => {
    if (!storymapMenu) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-popup]')) return
      setStorymapMenu(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [storymapMenu])

  const onNodesChange = useCallback((changes: NodeChange<FlowNode>[]) => {
    // Positions are saved when a drag ends, not on every frame of it.
    if (changes.some((change) => change.type === 'remove')) commit(removalGroup())
    else if (changes.some((change) => change.type === 'position' && change.dragging === false)) commit()
    setNodes((current) => applyNodeChanges(changes, current))
  }, [])

  const onEdgesChange = useCallback((changes: EdgeChange<FlowEdge>[]) => {
    if (changes.some((change) => change.type === 'remove')) commit(removalGroup())
    setEdges((current) => applyEdgeChanges(changes, current))
  }, [])

  const onConnect = useCallback((connection: Connection) => {
    commit()
    setEdges((current) => [...current, toFlowEdge({ id: newId(), source: connection.source, target: connection.target, label: '', type: null })])
  }, [])

  const isValidConnection = useCallback(
    (connection: Connection | FlowEdge) =>
      connection.source !== connection.target &&
      !edges.some((edge) => edge.source === connection.source && edge.target === connection.target),
    [edges],
  )

  const addNode = (shape: WorkflowNodeShape) => {
    if (nodes.length >= WORKFLOW_LIMITS.nodes) return
    const box = canvasRef.current?.getBoundingClientRect()
    const centre = box ? flow.screenToFlowPosition({ x: box.left + box.width / 2, y: box.top + box.height / 2 }) : { x: 0, y: 0 }
    const { id, x, y, shape: _shape, ...data } = newWorkflowNode(shape, centre.x, centre.y)
    commit()
    setNodes((current) => [...current.map((node) => ({ ...node, selected: false })), { id, type: shape, position: { x, y }, data, selected: true }])
    setEdges((current) => current.map((edge) => ({ ...edge, selected: false })))
  }

  const tidy = async () => {
    const positions = await tidyPositions(nodes, edges)
    commit()
    setNodes((current) => current.map((node) => ({ ...node, position: positions.get(node.id) ?? node.position })))
    requestAnimationFrame(() => void flow.fitView({ padding: 0.2, duration: 200 }))
  }

  const updateNode = (id: string, patch: Partial<NodeData>) => {
    commit(textGroup())
    setNodes((current) => current.map((node) => (node.id === id ? { ...node, data: { ...node.data, ...patch, updatedAt: nowIso() } } : node)))
  }

  const updateEdge = (id: string, patch: Partial<EdgeData>) => {
    commit(textGroup())
    setEdges((current) =>
      current.map((edge) => {
        if (edge.id !== id) return edge
        const data = { label: edge.data?.label ?? '', kind: edge.data?.kind ?? null, ...patch }
        return toFlowEdge({ id: edge.id, source: edge.source, target: edge.target, label: data.label, type: data.kind }, edge.selected)
      }),
    )
  }

  /** Saves the workflow first, then shows the storymap. */
  const openStorymap = async (boardId: string) => {
    await useWorkflowStore.getState().leave()
    await useBoardStore.getState().openBoard(boardId)
    setView('board')
  }

  const back = async () => {
    await useWorkflowStore.getState().leave()
    void useBoardStore.getState().showBoardsPage({})
    setView('boards')
  }

  if (phase === 'missing' || phase === 'error') {
    return (
      <div className={chrome.page}>
        <section className={chrome.state}>
          <h2>{phase === 'missing' ? 'That workflow no longer exists' : 'The workflow could not be loaded'}</h2>
          <Button variant="primary" onClick={() => void back()}>
            Back to boards
          </Button>
        </section>
      </div>
    )
  }

  const selectedNodes = nodes.filter((node) => node.selected)
  const selectedEdges = edges.filter((edge) => edge.selected)
  const selectedNode = selectedNodes.length === 1 && selectedEdges.length === 0 ? selectedNodes[0] : null
  // The project's labels as last read, plus those typed on this workflow since.
  const labels = selectedNode ? mergeLabels([projectLabels, nodeLabels(nodes.map((node) => node.data))]) : projectLabels
  const selectedEdge = selectedEdges.length === 1 && selectedNodes.length === 0 ? selectedEdges[0] : null

  return (
    <div className={chrome.page}>
      <header className={chrome.topBar}>
        <div className={chrome.row}>
          <button type="button" className={chrome.brand} onClick={() => void back()} title="Back to all boards">
            <img className={chrome.logo} src="/logo-mark.png" alt="" width={40} height={21} />
            <BrandLockup />
          </button>
          <span className={chrome.kind}>Workflow</span>
          {editable ? (
            <span className={chrome.name}>
              <InlineTitle
                value={name}
                label="Workflow name"
                maxLength={LIMITS.title}
                onCommit={(next) => useWorkflowStore.getState().change(toGraph(nodes, edges), next)}
              />
            </span>
          ) : (
            <span className={chrome.name}>{name} · view only</span>
          )}
          <span className={chrome.spacer} />
          <Button onClick={() => useUiStore.getState().setSwitcherOpen(true)} disabled={!workflowId}>
            Switch…
          </Button>
          <UserMenu />
        </div>
        <div className={chrome.row}>
          {editable ? (
            <>
              <Button onClick={() => addNode('step')}>+ Step</Button>
              <Button onClick={() => addNode('decision')}>+ Decision</Button>
              <Button onClick={() => addNode('start')}>+ Start</Button>
              <Button onClick={() => addNode('end')}>+ End</Button>
              <span className={chrome.separator} />
              <Button onClick={() => void tidy()} disabled={nodes.length === 0} title="Arrange the nodes left to right">
                Tidy layout
              </Button>
              <Button onClick={() => setGenerating(toGraph(nodes, edges))} disabled={!workflowId} title="Make a story map from this workflow, or update one made from it">
                Create storymap…
              </Button>
            </>
          ) : null}
          {links.boards.length > 0 ? (
            <span className={chrome.popupWrap} data-popup>
              <Button
                aria-haspopup="menu"
                aria-expanded={storymapMenu}
                title="Open a storymap generated from this workflow"
                onClick={() => setStorymapMenu((open) => !open)}
              >
                Open storymap ▾
              </Button>
              {storymapMenu ? (
                <div className={chrome.dropdown} role="menu">
                  {links.boards.map((board) => (
                    <MenuItem
                      key={board.id}
                      onClick={() => {
                        setStorymapMenu(false)
                        void openStorymap(board.id)
                      }}
                    >
                      {board.name}
                    </MenuItem>
                  ))}
                </div>
              ) : null}
            </span>
          ) : null}
          {editable ? (
            <>
              <span className={chrome.separator} />
              <Button onClick={() => restore(useWorkflowStore.getState().undo())} disabled={!canUndo} title="Undo (Ctrl+Z)">
                Undo
              </Button>
              <Button onClick={() => restore(useWorkflowStore.getState().redo())} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
                Redo
              </Button>
              <span className={chrome.separator} />
            </>
          ) : null}
          <Button onClick={() => void flow.fitView({ padding: 0.2, duration: 200 })}>Fit</Button>
        </div>
      </header>

      <div className={styles.body}>
        <div ref={canvasRef} className={chrome.canvasHost} data-testid="workflow-canvas">
          <ReactFlow<FlowNode, FlowEdge>
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            nodesDraggable={editable}
            nodesConnectable={editable}
            deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
            colorMode={mode}
            minZoom={0.2}
          >
            <Background />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
          </ReactFlow>
          {phase === 'loading' ? <div className={chrome.loading}>Loading workflow…</div> : null}
        </div>

        {selectedNode || selectedEdge ? (
          <aside
            className={styles.panel}
            aria-label="Details"
            onFocus={() => {
              typing.current += 1
            }}
          >
            {selectedNode ? (
              <NodeDetails
                key={selectedNode.id}
                node={selectedNode}
                labels={labels}
                targets={nodes.filter((node) => node.id !== selectedNode.id).map((node) => ({ id: node.id, label: node.data.title }))}
                readOnly={!editable}
                onChange={(patch) => updateNode(selectedNode.id, patch)}
                onDelete={() => void flow.deleteElements({ nodes: [{ id: selectedNode.id }] })}
              />
            ) : selectedEdge ? (
              <EdgeDetails
                key={selectedEdge.id}
                data={selectedEdge.data ?? { label: '', kind: null }}
                readOnly={!editable}
                onChange={(patch) => updateEdge(selectedEdge.id, patch)}
                onDelete={() => void flow.deleteElements({ edges: [{ id: selectedEdge.id }] })}
              />
            ) : null}
          </aside>
        ) : null}
      </div>

      <footer className={chrome.footer}>
        <span className={chrome.muted}>
          <a className={chrome.link} href="https://teamcentric.io" target="_blank" rel="noreferrer noopener">
            Teamcentric
          </a>{' '}
          &copy; 2026
        </span>
        <AppVersion className={chrome.muted} />
        <span className={chrome.spacer} />
        <WorkflowSaveStatus />
        <FeedbackTab />
      </footer>

      {generating && workflowId ? (
        <GenerateStorymapDialog workflowId={workflowId} name={name} graph={generating} onClose={() => setGenerating(null)} />
      ) : null}
    </div>
  )
}

function NodeDetails({
  node,
  labels,
  targets,
  readOnly,
  onChange,
  onDelete,
}: {
  node: FlowNode
  labels: ProjectLabels
  /** The other nodes, for relationships. */
  targets: readonly { id: string; label: string }[]
  readOnly: boolean
  onChange: (patch: Partial<NodeData>) => void
  onDelete: () => void
}) {
  // The title is required: an emptied field is kept locally and not saved until it has text again.
  const [title, setTitle] = useState(node.data.title)
  const core: NodeCore = { ...node.data, id: node.id }
  return (
    <>
      <h2 className={styles.panelTitle}>{WORKFLOW_NODE_SHAPE_LABELS[node.type ?? 'step']}</h2>
      <Field label="Title" error={title.trim() ? null : 'A title is required.'}>
        {(id) => (
          <TextInput
            id={id}
            value={title}
            readOnly={readOnly}
            maxLength={WORKFLOW_LIMITS.title}
            onChange={(event) => {
              setTitle(event.target.value)
              if (event.target.value.trim()) onChange({ title: event.target.value })
            }}
          />
        )}
      </Field>
      <Field label="Description">
        {(id) => (
          <TextArea
            id={id}
            rows={6}
            value={node.data.description}
            readOnly={readOnly}
            maxLength={WORKFLOW_LIMITS.description}
            onChange={(event) => onChange({ description: event.target.value })}
          />
        )}
      </Field>
      <Field label="Colour">
        {(id) =>
          readOnly ? (
            <span id={id}>{CARD_COLOUR_LABELS[node.data.colour]}</span>
          ) : (
            <ColourSwatches
              id={id}
              label="Colour"
              options={CARD_COLOURS.map((colour) => ({
                value: colour,
                label: CARD_COLOUR_LABELS[colour],
                colour: colour === 'default' ? null : CARD_COLOUR_HEX[colour],
              }))}
              value={node.data.colour}
              onPick={(colour) => onChange({ colour: colour as CardColour })}
            />
          )
        }
      </Field>
      <NodeCoreFields node={core} labels={labels} targets={targets} readOnly={readOnly} onChange={onChange} />
      {readOnly ? null : (
        <Button variant="danger" onClick={onDelete}>
          Delete node
        </Button>
      )}
    </>
  )
}

function EdgeDetails({
  data,
  readOnly,
  onChange,
  onDelete,
}: {
  data: EdgeData
  readOnly: boolean
  onChange: (patch: Partial<EdgeData>) => void
  onDelete: () => void
}) {
  return (
    <>
      <h2 className={styles.panelTitle}>Connection</h2>
      <Field label="Label" hint="For example Yes or No on a decision's options.">
        {(id) => (
          <TextInput
            id={id}
            value={data.label}
            readOnly={readOnly}
            maxLength={WORKFLOW_LIMITS.edgeLabel}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        )}
      </Field>
      <Field label="Type" hint="What the connection means. A typed connection becomes a relationship on the story map.">
        {(id) => (
          <Select
            id={id}
            value={data.kind ?? ''}
            disabled={readOnly}
            onChange={(event) => onChange({ kind: (event.target.value || null) as RelationshipType | null })}
          >
            <option value="">Flow (no type)</option>
            {RELATIONSHIP_TYPES.map((type) => (
              <option key={type} value={type}>
                {RELATIONSHIP_LABELS[type]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {readOnly ? null : (
        <Button variant="danger" onClick={onDelete}>
          Delete connection
        </Button>
      )}
    </>
  )
}

/**
 * Makes a story map from the workflow, or brings one made from it up to date: cards are
 * matched to their nodes by id, so nothing is added twice.
 */
function GenerateStorymapDialog({ workflowId, name, graph, onClose }: { workflowId: string; name: string; graph: WorkflowGraph; onClose: () => void }) {
  const [boards, setBoards] = useState<WorkflowLinks['boards'] | null>(null)
  const [mode, setMode] = useState<'new' | 'update'>('new')
  const [boardName, setBoardName] = useState(name)
  const [targetId, setTargetId] = useState('')
  const [summary, setSummary] = useState<UpdateSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // The boards already made from this workflow that the user may edit.
  useEffect(() => {
    let cancelled = false
    getRepository()
      .listWorkflowLinks(workflowId)
      .then((links) => {
        if (cancelled) return
        const editable = links.boards.filter((board) => canEditBoard(board.access))
        setBoards(editable)
        setTargetId(editable[0]?.id ?? '')
        if (editable.length > 0) setMode('update')
      })
      .catch(() => !cancelled && setBoards([]))
    return () => {
      cancelled = true
    }
  }, [workflowId])

  // For an update, the counts come from the board as it is now.
  useEffect(() => {
    if (mode !== 'update' || !targetId) return
    let cancelled = false
    setSummary(null)
    getRepository()
      .getBoard(targetId)
      .then((snapshot) => !cancelled && setSummary(planWorkflowUpdate(normalize(snapshot), graph).summary))
      .catch((problem: unknown) => !cancelled && setError(problem instanceof Error ? problem.message : 'That board could not be loaded.'))
    return () => {
      cancelled = true
    }
  }, [mode, targetId, graph])

  const submit = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      // The board is made from the saved workflow, so its cards match the stored nodes.
      await useWorkflowStore.getState().flush()
      const boardStore = useBoardStore.getState()
      if (mode === 'new') {
        const trimmed = boardName.trim()
        if (!trimmed) {
          setError('A board name is required.')
          return
        }
        const snapshot = workflowToSnapshot({ workflowId, graph, name: trimmed })
        await useWorkflowStore.getState().leave()
        const id = await boardStore.importSnapshot(snapshot)
        if (!id) throw new Error(useBoardStore.getState().error ?? 'The storymap could not be created.')
      } else {
        await useWorkflowStore.getState().leave()
        await boardStore.openBoard(targetId)
        const state = useBoardStore.getState().entities
        if (!state) throw new Error(useBoardStore.getState().error ?? 'That board could not be opened.')
        const { result } = planWorkflowUpdate(state, graph)
        if (result) useBoardStore.getState().run(() => result)
      }
      useUiStore.getState().setView('board')
      onClose()
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      title="Create storymap from workflow"
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={boards === null || busy || (mode === 'update' && !targetId)}>
            {busy ? 'Working…' : mode === 'new' ? 'Create storymap' : 'Update storymap'}
          </Button>
        </>
      }
    >
      {boards && boards.length > 0 ? (
        <fieldset className={chrome.choice}>
          <legend>This workflow already has a storymap</legend>
          <label>
            <input type="radio" name="workflow-generate-mode" checked={mode === 'update'} onChange={() => setMode('update')} /> Update an existing board
          </label>
          <label>
            <input type="radio" name="workflow-generate-mode" checked={mode === 'new'} onChange={() => setMode('new')} /> Create a new board
          </label>
        </fieldset>
      ) : null}

      {mode === 'new' ? (
        <>
          <Field label="Board name">
            {(id) => <TextInput id={id} data-autofocus value={boardName} maxLength={LIMITS.title} onChange={(event) => setBoardName(event.target.value)} />}
          </Field>
          <p className={chrome.dialogText}>
            Each of the {graph.nodes.length} nodes becomes a card, placed by its column group, column, swimlane and row. A node without a column goes
            to &quot;{UNPLACED_COLUMN_TITLE}&quot;; one without a swimlane to &quot;{WORKFLOW_SWIMLANE_TITLE}&quot;.
          </p>
        </>
      ) : (
        <>
          {boards && boards.length > 1 ? (
            <Field label="Board to update">
              {(id) => (
                <Select id={id} value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                  {boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : (
            <p className={chrome.dialogText}>Board: {boards?.[0]?.name}</p>
          )}
          <p className={chrome.dialogText}>
            {summary
              ? `${summary.updated} to update, ${summary.added} to add, ${summary.sourceRemoved} whose node is gone (kept, not deleted).`
              : 'Comparing with the board…'}
          </p>
          <p className={chrome.dialogText}>
            Cards already made from a node take its title, description, tags and colour, and its type, status, owner and icon when the node has
            them; they also gain the node&apos;s relationships and references they lack. A card moves only when its node&apos;s placement has
            changed. Anything added by hand is left alone.
          </p>
        </>
      )}
      {error ? <p className={chrome.dialogError}>{error}</p> : null}
    </Dialog>
  )
}

function WorkflowSaveStatus() {
  const status = useWorkflowStore((state) => state.saveStatus)
  const error = useWorkflowStore((state) => state.saveError)
  const lastSavedAt = useWorkflowStore((state) => state.lastSavedAt)
  const access = useWorkflowStore((state) => state.access)
  if (!canEditBoard(access)) return <span className={chrome.muted}>View only</span>
  if (status === 'conflict') {
    return (
      <span className={chrome.saveError} role="alert">
        Changed elsewhere — your latest edits are not saved.{' '}
        <Button onClick={() => void useWorkflowStore.getState().reload()}>Load latest</Button>
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className={chrome.saveError} role="alert">
        Save failed: {error}{' '}
        <Button onClick={() => useWorkflowStore.getState().retry()}>Retry</Button>
      </span>
    )
  }
  const text =
    status === 'saving' ? 'Saving…' : status === 'pending' ? 'Changed' : lastSavedAt ? `Saved ${new Date(lastSavedAt).toLocaleString()}` : ''
  return (
    <span className={chrome.muted} data-testid="workflow-save-status">
      {text}
    </span>
  )
}
